import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ITransactionService } from "@shared/interfaces/transaction.interface";
import { TransactionStatus } from "@shared/types/transactions/enum/transaction-status.enum";
import { TransactionDto } from "@shared/types/transactions/transaction.dto";
import { ColumnsType } from "antd/es/table";
import { useQuery } from "react-query";
import styles from "./Transactions.module.scss";
import { TransactionPlatform } from "@shared/types/transactions/enum/transaction-platform.enum";

const ALL = "All";

const ALL_OBJECT = { label: "All", value: ALL };

export const Statuses = [
  { label: "Completed", value: TransactionStatus.COMPLETED },
  { label: "Rejected", value: TransactionStatus.REJECTED },
];

export const Platforms = [
  { label: "iOS", value: TransactionPlatform.IOS },
  { label: "Android", value: TransactionPlatform.ANDROID },
];

const STATUSES = [{ ...ALL_OBJECT, label: "Filter by status" }, ...Statuses];
const PLATFORMS = [
  { ...ALL_OBJECT, label: "Filter by platform" },
  ...Platforms,
];

const getStatus = (type: TransactionDto["status"]) => {
  switch (type) {
    case TransactionStatus.COMPLETED:
      return (
        <Tag
          color="#009E60"
          style={{
            textTransform: "capitalize",
          }}
        >
          {TransactionStatus.COMPLETED}
        </Tag>
      );

    case TransactionStatus.REJECTED:
      return (
        <Tag
          color="#f5222d"
          style={{
            textTransform: "capitalize",
          }}
        >
          {TransactionStatus.REJECTED}
        </Tag>
      );

    default:
      return null;
  }
};

const getPlatformTag = (platform: TransactionDto["platform"]) => {
  switch (platform) {
    case TransactionPlatform.IOS:
      return (
        <Tag
          color="#007aff" // iOS Blue
          style={{
            textTransform: "capitalize",
          }}
        >
          iOS
        </Tag>
      );

    case TransactionPlatform.ANDROID:
      return (
        <Tag
          color="#3ddc84" // Android Green
          style={{
            textTransform: "capitalize",
          }}
        >
          Android
        </Tag>
      );

    case TransactionPlatform.OTHER:
      return (
        <Tag
          color="#8c8c8c" // Neutral Gray
          style={{
            textTransform: "capitalize",
          }}
        >
          Other
        </Tag>
      );

    default:
      return "N/A";
  }
};

const Transactions = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [searchText, setSearchText] = useState("");
  const [currentPageSize, setPageSize] = useState(20);
  const [containerHeight, setContainerHeight] = useState(0);
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const transactionsService = appInject<ITransactionService>(
    DI_TOKENS.transactionsService,
  );

  const columns = useMemo(
    () =>
      [
        {
          key: "id",
          dataIndex: "id",
          title: "ID",
          width: 300,
        },
        {
          key: "user",
          dataIndex: "user",
          title: "Purchased By",
          width: 180,
          render: (value: TransactionDto["user"]) =>
            `${value?.nickname ?? "N/A"} (${value?.phoneNumber ?? "N/A"})`,
        },
        {
          key: "plan",
          dataIndex: "plan",
          title: "Plan",
          width: 120,
          render: (value: TransactionDto["plan"]) => value?.name ?? "N/A",
        },
        {
          key: "revenueCatPurchaseId",
          dataIndex: "revenueCatPurchaseId",
          title: "Purchase Id",
          width: 160,
        },
        {
          key: "amount",
          dataIndex: "amount",
          title: "Amount Paid",
          render: (value: TransactionDto["amount"]) => `$${value}`,
          width: 140,
        },
        {
          key: "platform",
          dataIndex: "platform",
          title: "Platform",
          width: 100,
          render: getPlatformTag,
        },
        {
          key: "tokens",
          dataIndex: "plan",
          title: "Tokens Received",
          width: 150,
          render: (value: TransactionDto["plan"]) => value?.tokens ?? "N/A",
        },
        {
          key: "status",
          dataIndex: "status",
          title: "Status",
          width: 100,
          render: getStatus,
        },
        {
          key: "createdAt",
          dataIndex: "createdAt",
          title: "CreatedAt",
          render: (date: Date | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
          width: 200,
        },
      ] as ColumnsType<TransactionDto>,
    [],
  );

  const transactionsQuery = useQuery(
    [
      "fetch-transactions-list",
      page,
      currentPageSize,
      debouncedSearchText,
      status,
      platform,
    ],
    () =>
      transactionsService.list({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
        status: (status === ALL
          ? undefined
          : (status ?? undefined)) as TransactionStatus,
        platform: (platform === ALL
          ? undefined
          : (platform ?? undefined)) as TransactionPlatform,
      }),
    {
      refetchOnMount: true,
    },
  );

  const containerRef = useRef<any>();

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if ("offsetHeight" in containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  // Update debounced search text after a delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300); // Adjust debounce delay as needed

    // Clear the timeout if the effect re-runs (when searchText changes)
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  const resetFilters = () => {
    setSearchText("");
    setStatus(ALL);
    setPlatform(ALL);
  };

  return (
    <Layout className={styles.tableLayout}>
      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={7}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
            height: "100%",
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Transactions ({transactionsQuery.data?.data?.total})
          </Typography.Title>
        </Col>
        <Col>
          <Form.Item className="search-input-container">
            <Input
              onChange={handleSearch}
              value={searchText}
              name="search"
              style={{
                minWidth: 200,
              }}
              placeholder="Search by Name, Email, or Plan etc"
            />

            <Select
              style={{
                minWidth: 150,
              }}
              options={PLATFORMS}
              value={platform}
              onChange={setPlatform}
            />

            <Select
              style={{
                minWidth: 150,
              }}
              options={STATUSES}
              value={status}
              onChange={setStatus}
            />

            <Button type="primary" onClick={resetFilters}>
              Reset
            </Button>
          </Form.Item>
        </Col>
      </Row>
      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="key"
          dataSource={transactionsQuery.data?.data?.transactions || []}
          onChange={onTableChange}
          columns={columns}
          loading={transactionsQuery.isFetching}
          pagination={{
            current: page,
            total: transactionsQuery.data?.data?.total || 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight, x: "max-content" }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Transactions;
